import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { btnSecondaryClass } from "../../dashboardStyles";
import DeviceFrame, { DeviceToggle, initialDevice } from "../../../site-preview/DeviceFrame";
import { EMBED_MESSAGE_SOURCE, EMBED_OPEN_ATTRIBUTE, normalizeHex } from "../../../../utils/embed";
import { onBrandText } from "../../../../utils/color";

const FALLBACK_COLOR = "#5c9f24";

/** A grey bar standing in for text on the sketched site. */
const Bar = ({ className }) => <span className={`block rounded-full bg-ink-200 ${className}`} />;
Bar.propTypes = { className: PropTypes.string.isRequired };

/**
 * "This is how it looks on your site": a sketch of a business's website with
 * the real widget on it.
 *
 * Only the site is a sketch. The form is the live one (`/embed/:slug`, as on a
 * real site), the floating button copies the one `public/embed.js` draws —
 * same corner, shape, colour and label — and the window behaves like the real
 * one: the backdrop and the form's own close button shut it, the button opens
 * it, and it stays loaded between openings.
 */
const WidgetPreview = ({ slug, mode, ownButton, label, color }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const popupRef = useRef(null);
  const inlineRef = useRef(null);
  const replayRef = useRef(null);
  const [device, setDevice] = useState(() => initialDevice(null));
  const [open, setOpen] = useState(true);
  const [heights, setHeights] = useState({ popup: 560, inline: 560 });

  const popup = mode === "popup";
  const hex = normalizeHex(color) || FALLBACK_COLOR;
  const buttonStyle = { backgroundColor: hex, color: onBrandText(hex) };

  // The widget reports its height and its close button the way it does to a
  // business's page. Trusted only from our own frames, on our own origin.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.data?.source !== EMBED_MESSAGE_SOURCE) return;
      const which =
        event.source === popupRef.current?.contentWindow
          ? "popup"
          : event.source === inlineRef.current?.contentWindow
            ? "inline"
            : null;
      if (!which) return;
      if (event.data.type === "resize" && event.data.height > 0) {
        setHeights((current) => ({ ...current, [which]: Math.ceil(event.data.height) }));
      }
      if (event.data.type === "close" && which === "popup") setOpen(false);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => () => clearTimeout(replayRef.current), []);

  // Shut, then open again: the moment a visitor sees.
  const replay = () => {
    clearTimeout(replayRef.current);
    setOpen(false);
    replayRef.current = setTimeout(() => setOpen(true), 350);
  };

  const sketch = ({ width, height }) => {
    const wide = width >= 640;
    const pad = wide ? "px-10" : "px-5";

    return (
      <div className="relative overflow-hidden bg-white text-left font-sans text-ink-900" style={{ width, height }}>
        <div className="h-full overflow-y-auto" aria-hidden={popup && open ? "true" : undefined}>
          <div className={`flex items-center justify-between border-b border-ink-100 py-5 ${pad}`}>
            <span className="flex items-center gap-3">
              <span className="size-8 rounded-md bg-ink-200" />
              <span className="text-xl font-semibold text-ink-800">{t("Dashboard.MySite.widget.mock.business")}</span>
            </span>
            {wide ? (
              <span className="flex gap-9 text-sm text-ink-500">
                <span className="border-b-2 pb-1 font-semibold" style={{ borderColor: hex, color: hex }}>
                  {t("Dashboard.MySite.widget.mock.home")}
                </span>
                <span>{t("Dashboard.MySite.widget.mock.about")}</span>
                <span>{t("Dashboard.MySite.widget.mock.services")}</span>
                <span>{t("Dashboard.MySite.widget.mock.contact")}</span>
              </span>
            ) : (
              <i className="bi bi-list text-2xl text-ink-500" aria-hidden="true" />
            )}
          </div>

          <div className={`bg-ink-100 py-14 ${pad} ${wide ? "grid grid-cols-2 items-center gap-10" : ""}`}>
            <div>
              <p className="m-0 text-3xl leading-tight font-bold text-ink-800">
                {t("Dashboard.MySite.widget.mock.heroTitle")}
              </p>
              <Bar className="mt-6 h-3 w-4/5" />
              <Bar className="mt-3 h-3 w-3/5" />
              {popup && ownButton ? (
                <span className="relative mt-8 inline-block">
                  <button
                    type="button"
                    tabIndex={open ? -1 : 0}
                    onClick={() => setOpen(true)}
                    className="cursor-pointer rounded-lg px-6 py-3 text-base font-semibold outline-2 outline-offset-4 outline-dashed"
                    style={{ ...buttonStyle, outlineColor: hex }}
                  >
                    {label}
                  </button>
                  <span className="absolute -top-8 left-0 rounded-md bg-ink-900 px-2 py-0.5 font-mono text-xs whitespace-nowrap text-white">
                    {EMBED_OPEN_ATTRIBUTE}
                  </span>
                </span>
              ) : (
                <span className="mt-8 block h-12 w-40 rounded-lg" style={{ backgroundColor: hex, opacity: 0.85 }} />
              )}
            </div>
            {wide && <div className="h-56 rounded-2xl bg-ink-200" />}
          </div>

          {popup ? (
            <div className={`grid gap-6 py-12 ${pad} ${wide ? "grid-cols-3" : ""}`}>
              {[0, 1, 2].map((index) => (
                <div key={index} className="rounded-xl border border-ink-100 p-5">
                  <span className="block size-10 rounded-lg bg-ink-100" />
                  <Bar className="mt-5 h-3 w-3/4" />
                  <Bar className="mt-3 h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className={`py-12 ${pad}`}>
              <p className="m-0 text-2xl font-bold text-ink-800">{t("Dashboard.MySite.widget.mock.inlineTitle")}</p>
              <Bar className="mt-4 h-3 w-2/5" />
              <div className="mx-auto mt-8 max-w-[640px]">
                <iframe
                  ref={inlineRef}
                  src={`/embed/${encodeURIComponent(slug)}?mode=inline`}
                  title={t("Dashboard.MySite.widget.previewTitle")}
                  className="block w-full border-0 bg-transparent"
                  style={{ height: `${heights.inline}px` }}
                />
              </div>
            </div>
          )}

          <div className={`border-t border-ink-100 py-10 ${pad}`}>
            <Bar className="h-3 w-1/3" />
            <Bar className="mt-3 h-3 w-1/4" />
          </div>
        </div>

        {popup && !ownButton && (
          <button
            type="button"
            tabIndex={open ? -1 : 0}
            onClick={() => setOpen(true)}
            className="absolute right-5 bottom-5 z-10 cursor-pointer rounded-full px-5 py-3 text-[15px] leading-tight font-semibold shadow-[0_6px_20px_rgba(0,0,0,0.22)]"
            style={buttonStyle}
          >
            {label}
          </button>
        )}

        {popup && (
          // Kept mounted while shut, as the loader keeps its frame: opening it
          // again shows the form at once instead of loading it again.
          <div
            className={`absolute inset-0 z-20 flex overflow-y-auto bg-[rgba(15,20,25,0.6)] px-3 py-6 transition-opacity duration-300 motion-reduce:transition-none ${
              open ? "visible opacity-100" : "invisible opacity-0"
            }`}
            onClick={(event) => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
          >
            <div className="m-auto w-full max-w-[640px]">
              <iframe
                ref={popupRef}
                src={`/embed/${encodeURIComponent(slug)}?mode=popup`}
                title={t("Dashboard.MySite.widget.previewTitle")}
                className="block w-full border-0 bg-transparent"
                style={{ height: `${heights.popup}px` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      className="min-w-0 overflow-hidden rounded-card border border-ink-200 bg-white shadow-card"
      aria-labelledby={titleId}
      data-testid="widget-preview"
      data-device={device}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-ink-200 px-4 py-3 sm:px-5">
        <h2 id={titleId} className="m-0 text-base font-semibold text-ink-900">
          {t("Dashboard.MySite.widget.preview.title")}
        </h2>
        <DeviceToggle device={device} onChange={setDevice} />
      </div>

      <div className="bg-ink-50 p-3 sm:p-4">
        <DeviceFrame device={device} bar={{ dots: "traffic" }}>
          {sketch}
        </DeviceFrame>

        <div className="mt-3 flex flex-col items-center gap-2">
          {popup && (
            <button type="button" className={`${btnSecondaryClass} w-full`} onClick={replay}>
              <i className="bi bi-play-fill" aria-hidden="true" />
              {t("Dashboard.MySite.widget.preview.test")}
            </button>
          )}
          <p className="m-0 flex items-center gap-1.5 text-center text-xs text-ink-500">
            <i className="bi bi-info-circle" aria-hidden="true" />
            {t("Dashboard.MySite.widget.previewNote")}
          </p>
        </div>
      </div>
    </section>
  );
};

WidgetPreview.propTypes = {
  slug: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["popup", "inline"]).isRequired,
  /** The business's own button opens the window; there is no floating one. */
  ownButton: PropTypes.bool,
  label: PropTypes.string,
  /** The draft primary colour: the button's. The form reads the saved one. */
  color: PropTypes.string,
};

export default WidgetPreview;
